import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, User, Mail, Phone, Search } from "lucide-react";

const emptyContact = () => ({ name: "", email: "", phone: "" });

export default function AdminSuppliers() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || (user?.role === "projektleder" && (user?.departments || []).includes("indkoeb"));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [contacts, setContacts] = useState([emptyContact()]);
  const [toDelete, setToDelete] = useState(null);
  const [query, setQuery] = useState("");

  const load = () => api.get("/suppliers").then((r) => setItems([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "da", { sensitivity: "base" }))));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(""); setContacts([emptyContact()]); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id); setName(c.name);
    setContacts(c.contacts && c.contacts.length ? c.contacts.map((x) => ({ name: x.name || "", email: x.email || "", phone: x.phone || "" })) : [emptyContact()]);
    setOpen(true);
  };

  const setContact = (idx, patch) => setContacts((prev) => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const addContact = () => setContacts((prev) => [...prev, emptyContact()]);
  const removeContact = (idx) => setContacts((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) return toast.error("Leverandøren skal have et navn");
    const cleaned = contacts.filter((c) => c.name.trim() || c.email.trim() || c.phone.trim());
    const payload = { name: name.trim(), contacts: cleaned };
    try {
      if (editing) { await api.put(`/suppliers/${editing}`, payload); toast.success("Leverandør opdateret"); }
      else { await api.post("/suppliers", payload); toast.success("Leverandør oprettet"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try { await api.delete(`/suppliers/${toDelete.id}`); toast.success("Leverandør slettet"); setToDelete(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setToDelete(null); }
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((c) =>
    (c.name || "").toLowerCase().includes(q) ||
    (c.contacts || []).some((ct) => (ct.name || "").toLowerCase().includes(q) || (ct.email || "").toLowerCase().includes(q))
  ) : items;

  return (
    <div className="rise-in">
      <div className="flex items-end justify-between mb-8">
        <div><h1 className="text-4xl sm:text-5xl font-light tracking-tight text-[#1A1919]">Leverandører</h1></div>
        {canManage && <Button onClick={openNew} className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="new-supplier-btn"><Plus className="h-4 w-4 mr-2" /> Ny leverandør</Button>}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.75} />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søg efter leverandør eller kontaktperson…" className="pl-10 rounded-xl h-11" data-testid="supplier-search-input" />
      </div>

      {filtered.length === 0 && (
        <div className="pcard p-12 text-center" data-testid="supplier-empty-state">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} />
          <p className="mt-4 text-stone-500">{items.length === 0 ? "Ingen leverandører endnu." : `Ingen leverandører matcher "${query}".`}</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => (
          <div key={c.id} className="pcard pcard-hover p-6 flex flex-col" data-testid={`supplier-card-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 bg-[#F3F1EC] rounded-2xl flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-stone-500" strokeWidth={1.75} /></div>
              <p className="font-semibold text-[#1A1919] truncate">{c.name}</p>
            </div>
            <div className="mt-5 space-y-3 flex-1">
              {(c.contacts && c.contacts.length) ? c.contacts.map((ct, i) => (
                <div key={i} className="border border-[#EBE8E3] rounded-xl px-3 py-2.5">
                  {ct.name && <p className="text-sm font-medium text-[#1A1919] flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.75} /> {ct.name}</p>}
                  {ct.email && <p className="text-xs text-stone-500 mt-1 flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} /> {ct.email}</p>}
                  {ct.phone && <p className="text-xs text-stone-500 mt-1 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} /> {ct.phone}</p>}
                </div>
              )) : <p className="text-sm text-muted-foreground">Ingen kontaktpersoner</p>}
            </div>
            <div className="flex gap-2 mt-5">
              {canManage ? (
                <>
                  <Button size="sm" variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => openEdit(c)} data-testid={`edit-supplier-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Rediger</Button>
                  <Button size="sm" variant="ghost" className="rounded-xl text-destructive hover:text-destructive" onClick={() => setToDelete(c)} data-testid={`delete-supplier-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </>
              ) : <span className="text-xs text-muted-foreground">Skrivebeskyttet</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-[#F1DEDA] text-[#A83232] flex items-center justify-center shrink-0"><Building2 className="h-5 w-5" strokeWidth={1.75} /></span>
              <div>
                <DialogTitle className="text-xl font-semibold leading-tight">{editing ? "Rediger leverandør" : "Ny leverandør"}</DialogTitle>
                <p className="text-sm text-stone-500">{editing ? "Opdatér leverandørens navn og kontaktpersoner" : "Opret en leverandør med kontaktpersoner"}</p>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-5 mt-1">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Leverandørnavn</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                <Input data-testid="supp-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl h-11 pl-9 bg-white" placeholder="Fx Stål & Co ApS" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="label-caps">Kontaktpersoner</p>
                <span className="text-xs font-medium text-stone-400">{contacts.length}</span>
              </div>
              <div className="space-y-3">
                {contacts.map((ct, idx) => (
                  <div key={idx} className="rounded-2xl border border-[#EBE8E3] bg-[#FBFAF8] p-3.5 space-y-3" data-testid={`supp-contact-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-xs font-semibold text-stone-600">
                        <span className="h-6 w-6 rounded-full bg-[#A83232]/10 text-[#A83232] text-[11px] font-semibold flex items-center justify-center">{(ct.name || "").trim().slice(0, 1).toUpperCase() || (idx + 1)}</span>
                        Kontakt {idx + 1}
                      </span>
                      {contacts.length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-stone-400 hover:text-destructive hover:bg-[#F1DEDA]" onClick={() => removeContact(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </div>
                    <div className="space-y-2">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                        <Input placeholder="Navn" value={ct.name} onChange={(e) => setContact(idx, { name: e.target.value })} data-testid={`supp-contact-name-${idx}`} className="rounded-xl h-10 pl-9 bg-white" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                          <Input placeholder="Email" value={ct.email} onChange={(e) => setContact(idx, { email: e.target.value })} className="rounded-xl h-10 pl-9 bg-white" />
                        </div>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                          <Input placeholder="Telefon" value={ct.phone} onChange={(e) => setContact(idx, { phone: e.target.value })} className="rounded-xl h-10 pl-9 bg-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addContact} data-testid="supp-add-contact-btn" className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#D6D3D1] text-stone-500 hover:text-[#A83232] hover:border-[#A83232] hover:bg-[#F1DEDA]/40 [transition-property:color,border-color,background-color] duration-200 py-2.5 text-sm font-medium">
                <Plus className="h-4 w-4" /> Tilføj kontaktperson
              </button>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setOpen(false)}>Annullér</Button>
            <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" onClick={save} data-testid="save-supplier-btn">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-supplier-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-medium">Slet leverandør</AlertDialogTitle>
            <AlertDialogDescription>Er du sikker på, at du vil slette "{toDelete?.name}"? Denne handling kan ikke fortrydes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-supplier">Slet leverandør</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
